/**
 * Fin de round réussi en **mode illimité** (DECISIONS §7.6, A-25).
 *
 * Le montant affiché est **exclusivement** celui renvoyé par le serveur à la clôture de la vague
 * (`versement`) : il n'est jamais recalculé à partir du solde de run, du bonus de fin de niveau ou
 * d'une constante du client. Le solde de run affiché vient lui aussi de la réponse serveur
 * (`eclatsRun`), qui a déjà été remis à son niveau de départ pour le round suivant.
 *
 * L'écran ne décide de rien : il annonce le versement, puis laisse le joueur continuer (le round
 * suivant est déjà ouvert côté serveur, la vague suivante est prête).
 */
import { useTranslation } from 'react-i18next';

import { formaterNombre } from '../i18n';
import { Bouton } from './Bouton';

export interface ProprietesPanneauFinDeRound {
  /** Round qui vient d'être réussi (1-based). */
  readonly roundTermine: number;
  /** Round qui s'ouvre (1-based). */
  readonly roundSuivant: number;
  /** Montant versé au solde persistant, **tel que renvoyé par le serveur**. */
  readonly versement: number;
  /** Solde de run après versement, **tel que renvoyé par le serveur**. */
  readonly eclatsRun: number;
  readonly onContinuer: () => void;
}

export function PanneauFinDeRound({
  roundTermine,
  roundSuivant,
  versement,
  eclatsRun,
  onContinuer,
}: ProprietesPanneauFinDeRound) {
  const { t, i18n } = useTranslation();
  const nombre = (valeur: number): string => formaterNombre(valeur, i18n.language);

  return (
    <section
      className="panneau panneau--fin-de-round"
      role="status"
      aria-labelledby="titre-fin-de-round"
      data-fin-de-round={roundTermine}
      data-versement={versement}
      data-eclats-run={eclatsRun}
    >
      <h2 className="panneau__titre" id="titre-fin-de-round">
        {t('jeu.illimite.finDeRound.titre', { numero: nombre(roundTermine) })}
      </h2>
      <p className="note">{t('jeu.illimite.finDeRound.texte')}</p>

      <dl className="hud__grille">
        <div className="hud__case">
          <dt className="hud__etiquette">{t('jeu.illimite.finDeRound.verse')}</dt>
          <dd className="hud__valeur" data-versement-affiche={versement}>
            {nombre(versement)}
          </dd>
        </div>
        <div className="hud__case">
          <dt className="hud__etiquette">{t('jeu.illimite.finDeRound.soldeRun')}</dt>
          <dd className="hud__valeur" data-solde-run-apres={eclatsRun}>
            {nombre(eclatsRun)}
          </dd>
        </div>
      </dl>

      <Bouton variante="principal" onClick={onContinuer}>
        {t('jeu.illimite.finDeRound.suivant', { numero: nombre(roundSuivant) })}
      </Bouton>
    </section>
  );
}
